import angular from 'angular';
import isUnscheduledShift from '@sdk/shifts/utils/isUnscheduledShift';
import gettext from '@sdk/lang/gettext';
import { AttestationOption } from '@sdk/automatedBreaks/types/Attestation';

import SelectableSearchableSortableListCtrl from '../dashboard/manage/SelectableSearchableSortableListCtrl';
import { LARGE_CHAR } from '../constants';
import {
  FILTERS_BAR_HEIGHT,
  TIMEZONE_BANNER_HEIGHT,
  USERS_LIST_GROUPS_COLLAPSE_THRESHOLD,
} from '../dashboard/constants';

export default class ReportsTableBaseCtrl extends SelectableSearchableSortableListCtrl {
  unitFormatters = {
    currency: (v) => this.moneyDisplayFilter(v),
    minutes: (v, schemaItem) =>
      this.mapMinutesCell(v, {
        showDurationsAsDecimals: this.showDurationsAsDecimals,
        delta: schemaItem?.delta,
        displayEmptyWhenZero: schemaItem?.displayEmptyWhenZero,
      }),
    hours: (v, schemaItem) =>
      this.mapMinutesCell(v * 60, {
        showDurationsAsDecimals: this.showDurationsAsDecimals,
        displayEmptyWhenZero: schemaItem?.displayEmptyWhenZero,
      }),
    date: (v) => this.toDateFilter(v),
    'date:no-year-if-current': (v) => this.toDateFilter(v),
    time: (v) => this.toTimeFilter(v),
    '%': (v) => `${v || 0}%`,
    none: (v) => v,
  };

  valueArrayFormatter =
    (fieldName, formatter, { handleIsMissed = false } = {}) =>
    (group, c, index, groupTotals) => {
      if (group.isGroupHeader) {
        return this._getCellObject(c, index, group, groupTotals).value;
      }

      if (!group[fieldName]) {
        return '';
      }

      const schemaItem = this.schema.items.find((i) => i.property === c.property);

      return group[fieldName]
        .map(({ value, isMissed, attestationOption, skipFormatting }) => {
          if (skipFormatting) {
            return value;
          }

          if (handleIsMissed && isMissed) {
            if (attestationOption === AttestationOption.waived) {
              return gettext('Waived');
            }

            if (attestationOption !== AttestationOption.taken) {
              return gettext('Missed');
            }
          }

          return this.unitFormatters[formatter](value, schemaItem);
        })
        .join('\n');
    };

  exportCustomPropertiesDict = {
    employee: (group) => (group.isGroupHeader ? this.getGroupName(group) : group.user.fullname),
    name: (group) => this.getRowName(group),
    employeeId: (group) => (!group.isGroupHeader ? group.user.employeeId : ''),

    isAutoClockOut: this.valueArrayFormatter('isAutoClockOutArray', 'none'),
    breakType: this.valueArrayFormatter('breakTypeArray', 'none'),

    // time
    clockedIn: this.valueArrayFormatter('clockedInArray', 'time'),
    clockedOut: this.valueArrayFormatter('clockedOutArray', 'time'),
    breakStart: this.valueArrayFormatter('breakStartArray', 'time'),
    breakEnd: this.valueArrayFormatter('breakEndArray', 'time'),

    // minutes
    actual: this.valueArrayFormatter('actualDurationArray', 'minutes'),
    break: this.valueArrayFormatter('breakDurationArray', 'minutes', {
      handleIsMissed: true,
    }),
    scheduledBreakDuration: this.valueArrayFormatter('scheduledBreakDurationArray', 'minutes'),

    groups: (group, c, index, groupTotals) => {
      if (group.isGroupHeader) {
        return this._getCellObject(c, index, group, groupTotals).value;
      }

      return group.user.groups.map((g) => g.name).join('\n');
    },
  };

  ROW_MIN_HEIGHT = 60;
  ROW_PADDING_VERTICAL = 20;
  ROW_BORDER_HEIGHT = 1;
  ROW_INNER_TEXT_LINE_HEIGHT = 20;
  COLLAPSE_BUTTON_ROW_HEIGHT = 20;

  constructor($injector, searchProp, registerInvalidate) {
    'ngInject';

    const filterFilter = $injector.get('filterFilter');
    const $filter = $injector.get('$filter');
    const $parse = $injector.get('$parse');
    const $rootScope = $injector.get('$rootScope');
    const Utils = $injector.get('Utils');
    const ShiftUtils = $injector.get('ShiftUtils');
    const SlingAnalytics = $injector.get('SlingAnalytics');

    super(filterFilter, $parse, searchProp, registerInvalidate);
    Object.assign(this, {
      Utils,
      ShiftUtils,
      SlingAnalytics,
      $parse,
      $rootScope,
      filterFilter,
      toDateFilter: $filter('toDate'),
      toTimeFilter: $filter('toTime'),
      orderByFilter: $filter('orderBy'),
      moneyDisplayFilter: $filter('moneyDisplay'),
    });
  }

  $onInit() {
    this.getDataForExport = this._getDataForExport.bind(this); // hardbind and bind out
    this.setInitialSortMethod();
    if (this.tableHeaderLabels && !('name' in this.tableHeaderLabels)) {
      this.tableHeaderLabels.name = this._getNameColumnLabel();
    }
  }

  getRowName(row) {
    return row.isGroupHeader ? this.getGroupName(row) : row.user.fullname;
  }

  setInitialSortMethod() {
    if (!this.sortMethod) {
      this.sortMethod = 'user.fullname';
    }
  }

  // This is designed for the reports with grouping option
  _getDataForExport() {
    const { reportExported } = this.SlingAnalytics.analyticsEvents;
    this.SlingAnalytics.track(reportExported, this.reportType);

    this._setColumnsForExport();

    return {
      header: this._columnsForExport.map((c) => this.tableHeaderLabels[c.property]?.toUpperCase()),
      body: this.orderByFilter(this.aggregatedEvents, ['keySort', 'isGroupHeader', this.sortMethod])
        .filter((g) => g.isGroupHeader || !this.isGroupCollapsed(g))
        .reduce(this._rowsReducer, []),
      footer: this._columnsForExport.map((c, i) =>
        this._mapTotalsField(this.totals, c.property, i)
      ),
    };
  }

  _setColumnsForExport() {
    const reportColumns = this.reportColumns || this.columns.reportColumns;
    this._columnsForExport = reportColumns.filter(
      (c) => c.active && this.canDataColumnBeVisible(c.property)
    );
    this._columnsForExport.unshift({ property: 'name' });
  }

  _getNameColumnLabel() {
    return 'legal name';
  }

  _rowsReducer = (acc, row) => {
    const data = this._getDataRowForExport(row);

    const groupTotals = {};
    const dataToExport = data.map((group) =>
      this._columnsForExport.map((c, index) => {
        if (c.property in this.exportCustomPropertiesDict) {
          return {
            value: this.exportCustomPropertiesDict[c.property](group, c, index, groupTotals),
          };
        }

        return this._getCellObject(c, index, group, groupTotals, row);
      })
    );

    dataToExport.forEach((arr) => {
      acc.push(
        arr.map((item, index) =>
          item.schemaItem?.groupTotal && item.schemaItem?.formula
            ? this._formatUnit(item.schemaItem, groupTotals[index])
            : item.value
        )
      );
    });

    return acc;
  };

  _getCellObject(c, index, group, groupTotals, row) {
    const schemaItem = this.schema.items.find((i) => i.property === c.property);
    const value = this._mapSchemaField(group, c.property, schemaItem, row);

    if (schemaItem.groupTotal && schemaItem.formula) {
      groupTotals[index] =
        (groupTotals[index] || 0) +
        this.Utils.evaluateExpression(schemaItem.formula, { data: group });
    }

    return {
      value,
      schemaItem,
    };
  }

  _getDataRowForExport(row) {
    return row.isGroupHeader || this.totalsModeEnabled
      ? [
          this.Utils.extendWithProps(row, { ...(row.totals || row) }, [
            'isGroupHeader',
            'key',
            'groupType',
          ]),
        ]
      : this._getUserRow(row);
  }

  _getUserRow(row) {
    return row.userRows;
  }

  _shouldDisplayEmptyCell(schemaItem, row) {
    return row.isGroupHeader && !schemaItem.sumUp;
  }

  _formatUnit(schemaItem, value) {
    if (schemaItem.unit in this.unitFormatters) {
      return this.unitFormatters[schemaItem.unit](value, schemaItem);
    }

    return value;
  }

  _evaluateEmptyValue(schemaItem, item) {
    return this.Utils.evaluateExpression(schemaItem?.emptyValue || '', {
      options: {
        durationsAsDecimals: this.showDurationsAsDecimals,
        isPastDtendWithNoClockIn: item.isPastDtendWithNoClockIn ?? false,
      },
    });
  }

  _mapSchemaField(item, field, schemaItem, row) {
    let mapped;
    if (this._shouldDisplayEmptyCell(schemaItem, item)) {
      mapped = ' ';
    } else if (schemaItem?.linkedPosition || schemaItem?.linkedLocation) {
      mapped = item[field]?.name ?? '-';
    } else if (schemaItem?.showEmptyValueIfUnscheduled) {
      mapped = isUnscheduledShift(row)
        ? this._evaluateEmptyValue(schemaItem, item)
        : this._formatUnit(schemaItem, item[field]);
    } else {
      mapped = this._formatUnit(schemaItem, item[field]);
    }

    return mapped || this._evaluateEmptyValue(schemaItem, item) || '';
  }

  _mapTotalsField(item, field, index) {
    if (index === 0) {
      return 'TOTAL';
    }

    const schemaItem = this.schema.items.find((i) => i.property === field);
    if (!schemaItem?.includeForTotals) {
      return '';
    }

    return this._mapSchemaField(item, field, schemaItem);
  }

  getSortMethod(columnId) {
    if (columnId === 'employeeId') {
      return 'user.employeeId';
    }

    const schemaItem = this.schema.items.find((i) => i.property === columnId);
    if (this.totalsModeEnabled || schemaItem?.groupTotal) {
      return `totals.${this.sortMethods[columnId]}`;
    }

    return this.sortMethods[columnId];
  }

  _setSortMethods() {
    const sortMethodsReducer = (agg, item) => ((agg[item.property] = item.property), agg);

    this._allSortMethods = this.schema.items.reduce(sortMethodsReducer, {});

    this._combinationsSortMethods = this.schema.items
      .filter((i) => i.groupTotal)
      .reduce(sortMethodsReducer, {});
  }

  get stickyBarOffset() {
    return (
      this.$rootScope.navbarHeight +
      112 + // title
      (this.$rootScope.timezoneDiscrepancyShown ? TIMEZONE_BANNER_HEIGHT : 0) +
      (this.filtersOpened ? FILTERS_BAR_HEIGHT : 0) +
      (this.statsOpened ? 56 : 0)
    );
  }

  mapMinutesCell(value, { showDurationsAsDecimals, delta, displayEmptyWhenZero }) {
    if (value === '') {
      return value;
    }

    if (!value) {
      if (value === 0 && displayEmptyWhenZero) {
        return '';
      }

      if (value === 0 && delta) {
        return showDurationsAsDecimals ? 0 : '0min';
      }

      return '-';
    }

    const decimalFormat = this.Utils.getDecimalFormat();
    const format = (number) => this.Utils.formatNumber(number, decimalFormat);
    const duration = (minutes) =>
      this.Utils.getDisplayDuration(minutes, decimalFormat, undefined, delta, delta);

    return showDurationsAsDecimals
      ? format(this.Utils.roundNumber(value / 60))
      : duration(Math.round(value));
  }

  getGroupName(group) {
    if (group.groupType === 'date') {
      return group.key;
    }

    // matches: noPosition, noLocation, noXXX
    if (/^no[A-Z]\w*$/.test(group.key)) {
      // returns: NO POSITION, NO LOCATION, NO XXX
      return `NO ${group.key.match(/^no([A-Z]\w*)$/)[1].toUpperCase()}`;
    }

    // we can fallback to keySort which is the human readable string used
    // for sorting: https://github.com/getsling/sling-issues/issues/3262
    return this.ShiftUtils.getGroupTitle(group) || group.keySort;
  }

  getGroupHeaderClass(group) {
    if (!group.isGroupHeader) {
      return '';
    }

    const persona = this.PersonaCollection.personasDict[group.key];

    if (persona?.type === 'position') {
      return `reports-group-header ${persona.color.replace('#', 'g')}`;
    }

    return 'reports-group-header';
  }

  toggleGroupCollapse(group) {
    const groupIndex = this.collapsedGroups.findIndex((key) => key === group.key);
    if (groupIndex < 0) {
      this.collapsedGroups.push(group.key);
    } else {
      this.collapsedGroups.splice(groupIndex, 1);
    }

    this.$rootScope.$broadcast('vsRepeatTrigger');
  }

  isGroupCollapsed(group) {
    return this.collapsedGroups.includes(group.groupKey);
  }

  _groupByUser = (rows, event) => {
    const keys = this.getGroupObjectKeys(event);
    keys.forEach((key) => {
      const groupHeaderIndex = this._getGroupIndex(rows, key, this.type);
      const { user, location, position } = event;
      let userIndex = rows.findIndex((row) => row.key === `${key.value}_${user.id}`);
      if (userIndex < 0) {
        rows.push({
          user,
          key: `${key.value}_${user.id}`,
          groupKey: key.value,
          keySort: key.sort,
          totals: {},
          userRows: [],
        });

        userIndex = rows.length - 1;
      }

      if (groupHeaderIndex !== null) {
        rows[groupHeaderIndex].totals = this.calculateTotals(rows[groupHeaderIndex].totals, event);
      }

      rows[userIndex].totals = this.calculateTotals(rows[userIndex].totals, event);
      rows[userIndex].userRows.push({
        ...event,
        position: this.PersonaCollection.personasDict[position.id],
        location: this.PersonaCollection.personasDict[location.id],
      });
    });

    return rows;
  };

  _getGroupKeys(event, allowGroupingByUsers) {
    return {
      user: [allowGroupingByUsers ? event.user?.id : 'all'],
      date: [typeof event.date === 'object' ? this.toDateFilter(event.date) : event.date],
      location: [event.location?.id || 'noLocation'],
      position: [event.position?.id || 'noPosition'],
      group: event.user?.groups.length ? event.user.groups.map((g) => g.id) : ['noGroups'],
    }[this.type];
  }

  // eslint-disable-next-line complexity
  _getGroupKeySortProps(event, allowGroupingByUsers) {
    switch (this.type) {
      case 'user':
        return [allowGroupingByUsers ? event.user?.fullname : 'all'];
      case 'date':
        return [
          this.Utils.dateFromHours(
            '00:00:00',
            typeof event.date === 'object' ? event.date : this.Utils.getRealDate(event.date)
          ),
        ];
      case 'location':
        return [event.location?.name || LARGE_CHAR];
      case 'position':
        return [event.position?.name || LARGE_CHAR];
      case 'group':
        return event.user?.groups.length ? event.user.groups.map((g) => g.name) : [LARGE_CHAR];
      default:
        throw 'unknown type';
    }
  }

  getGroupObjectKeys(event, { allowGroupingByUsers = false } = {}) {
    const keys = this._getGroupKeys(event, allowGroupingByUsers);
    const sortProps = this._getGroupKeySortProps(event, allowGroupingByUsers);
    return keys.map((value, index) => ({
      value,
      sort: sortProps[index],
    }));
  }

  _getGroupIndex(rows, { value: key, sort }, groupType) {
    let groupHeaderIndex = key !== 'all' ? rows.findIndex((g) => g.key === key) : null;
    const totals = this._getEmptyTotals();

    if (groupHeaderIndex < 0) {
      rows.push({
        key,
        totals,
        groupKey: key,
        keySort: sort,
        isGroupHeader: true,
        groupType,
      });

      groupHeaderIndex = rows.length - 1;
    }

    return groupHeaderIndex;
  }

  _getEmptyTotals() {
    // can be overriden by child
    return {};
  }

  calculateTotals(totals, event) {
    return this._calculateAggregatedTotals(this._sumTotals(totals, event));
  }

  _sumTotals(totals, event) {
    Object.keys(event).forEach((property) => {
      const schemaItem = this.schema.items.find((i) => i.property === property);
      if (schemaItem && schemaItem.statusField && !event.shift?.id) {
        // PTO events should be ignored when setting the status field
        return;
      }

      if ((!schemaItem && angular.isNumber(event[property])) || schemaItem?.sumUp) {
        totals[property] = (totals[property] || 0) + event[property];
      } else {
        totals[property] = event[property];
      }
    });

    this.schema.items
      .filter((i) => i.formula && i.groupTotal)
      .forEach((schemaItem) => {
        if (!schemaItem.sumUp) {
          if (schemaItem.property in totals) {
            return;
          }

          totals[schemaItem.property] = this.Utils.evaluateExpression(schemaItem.formula, {
            data: event,
          });
          return;
        }

        totals[schemaItem.property] =
          (totals[schemaItem.property] || 0) +
          this.Utils.evaluateExpression(schemaItem.formula, { data: event });
      });
    return totals;
  }

  _toggleCollapse() {
    if (this.collapsedGroups.length) {
      this.collapsedGroups.length = 0;
    } else {
      this.collapsedGroups = this.aggregatedEvents.filter((e) => e.isGroupHeader).map((e) => e.key);
    }

    this.$rootScope.$broadcast('vsRepeatTrigger');
  }

  _updateCollapsedGroups() {
    const currentGroups = this.aggregatedEvents.filter((e) => e.isGroupHeader).map((e) => e.key);

    this.collapsedGroups = this.collapsedGroups.filter((g) => currentGroups.includes(g));
  }

  _calculateAggregatedTotals(totals) {
    this.schema.items
      .filter((s) => s.calculateOnAggregated)
      .forEach((schemaItem) => {
        totals[schemaItem.property] = this.Utils.evaluateExpression(schemaItem.formula, {
          sales: this.sales,
          data: totals,
        });
      });

    return totals;
  }

  getStickyCellHeight(group) {
    const HEADER_ROW_HEIGHT = 40;
    if (group.isGroupHeader) {
      return HEADER_ROW_HEIGHT;
    }

    if (this.collapsedGroups.includes(group.groupKey)) {
      return 0;
    }

    const MIN_HEIGHT = 60;
    const PADDING_VERTICAL = 20;
    const TEXT_HEIGHT = 20;
    const BORDER_HEIGHT = 1;
    const height = group.userRows.length * TEXT_HEIGHT + PADDING_VERTICAL + BORDER_HEIGHT;

    return Math.max(
      this._getGroupsCellHeight(group.user, group.totals || group, this.columns.reportColumns),
      MIN_HEIGHT,
      height
    );
  }

  _getGroupsCellHeight(user, data, reportColumns) {
    if (!reportColumns.find((c) => c.property === 'groups' && c.active) || !user.groups?.length) {
      return 0;
    }

    const isExtendingGroupsActive = user.groups.length > USERS_LIST_GROUPS_COLLAPSE_THRESHOLD;
    const buttonHeight = isExtendingGroupsActive ? this.COLLAPSE_BUTTON_ROW_HEIGHT : 0;
    let groupsCount;
    if (!isExtendingGroupsActive) {
      groupsCount = user.groups.length;
    } else {
      groupsCount =
        !('$$groupsExpanded' in data) || !data.$$groupsExpanded
          ? USERS_LIST_GROUPS_COLLAPSE_THRESHOLD
          : user.groups.length;
    }

    return (
      groupsCount * this.ROW_INNER_TEXT_LINE_HEIGHT +
      buttonHeight +
      this.ROW_PADDING_VERTICAL +
      this.ROW_BORDER_HEIGHT
    );
  }
}
